import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { paymentPlanSchema, type PaymentPlanFormData } from '../schemas/paymentPlanSchema'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Textarea } from '@/components/ui/Textarea'
import { Button } from '@/components/ui/Button'

interface PaymentPlanFormProps {
  initialData?: any
  onSubmit: (data: PaymentPlanFormData) => Promise<void>
  onCancel: () => void
  loading?: boolean
}

export function PaymentPlanForm({ initialData, onSubmit, onCancel, loading }: PaymentPlanFormProps) {
  const { register, handleSubmit, formState: { errors } } = useForm<PaymentPlanFormData>({
    resolver: zodResolver(paymentPlanSchema),
    defaultValues: initialData,
  })

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <Input
          label="Total Amount"
          type="number"
          step="0.01"
          {...register('total_amount')}
          error={errors.total_amount?.message}
          required
        />
        <Input
          label="Installment Amount"
          type="number"
          step="0.01"
          {...register('installment_amount')}
          error={errors.installment_amount?.message}
          required
        />
      </div>

      <Select
        label="Frequency"
        {...register('frequency')}
        options={[
          { value: 'weekly', label: 'Weekly' },
          { value: 'biweekly', label: 'Bi-weekly' },
          { value: 'monthly', label: 'Monthly' },
          { value: 'harvest', label: 'At Harvest' },
          { value: 'custom', label: 'Custom' },
        ]}
        error={errors.frequency?.message}
        required
      />

      <div className="grid grid-cols-2 gap-4">
        <Input
          label="Start Date"
          type="date"
          {...register('start_date')}
          error={errors.start_date?.message}
          required
        />
        <Input
          label="End Date (Optional)"
          type="date"
          {...register('end_date')}
          error={errors.end_date?.message}
        />
      </div>

      <Textarea
        label="Notes"
        {...register('notes')}
        error={errors.notes?.message}
      />

      <div className="flex justify-end space-x-3 pt-4">
        <Button type="button" variant="secondary" onClick={onCancel}>Cancel</Button>
        <Button type="submit" loading={loading}>Create Payment Plan</Button>
      </div>
    </form>
  )
}
